import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Plus, Search, Building2, Eye, Pause, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

const TenantList = () => {
  const [tenants, setTenants] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const limit = 10;
  const navigate = useNavigate();

  const fetchTenants = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/super-admin/tenants', { params: { page, limit, search, status: statusFilter } });
      setTenants(res.data.tenants);
      setTotal(res.data.total);
    } catch (e) {}
    setLoading(false);
  }, [page, search, statusFilter]);

  useEffect(() => { fetchTenants(); }, [fetchTenants]);

  const handleSuspend = async (id) => {
    if (!window.confirm('Suspend this tenant?')) return;
    try {
      await api.patch(`/super-admin/tenants/${id}/suspend`);
      toast.success('Tenant suspended');
      fetchTenants();
    } catch (e) {}
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tenants</h1>
          <p className="text-gray-500 text-sm mt-1">{total} total organizations</p>
        </div>
        <Link to="/super-admin/tenants/new" className="btn-primary">
          <Plus className="w-4 h-4" /> Onboard New Tenant
        </Link>
      </div>

      {/* Filters */}
      <div className="card !p-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input className="input pl-9" placeholder="Search by name, slug, email..." value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }} />
        </div>
        <select className="input w-auto" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="trial">Trial</option>
          <option value="suspended">Suspended</option>
        </select>
        <button onClick={fetchTenants} className="btn-secondary"><RefreshCw className="w-4 h-4" /></button>
      </div>

      {/* Table */}
      <div className="card !p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Organization', 'Slug', 'Plan', 'Status', 'Contact', 'Users/Storage', 'Created', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={8} className="text-center py-12 text-gray-400">Loading...</td></tr>
              ) : tenants.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-12 text-gray-400">
                  <Building2 className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                  No tenants found. <Link to="/super-admin/tenants/new" className="text-primary-600 hover:underline">Create the first one.</Link>
                </td></tr>
              ) : tenants.map(t => (
                <tr key={t.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center font-bold text-primary-700 text-xs flex-shrink-0">
                        {t.name[0].toUpperCase()}
                      </div>
                      <span className="font-medium text-gray-900">{t.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-500 font-mono text-xs">{t.slug}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      t.plan === 'enterprise' ? 'bg-purple-100 text-purple-800' :
                      t.plan === 'professional' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-700'
                    }`}>{t.plan}</span>
                  </td>
                  <td className="px-4 py-3"><span className={`badge-${t.status}`}>{t.status}</span></td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{t.contact_email}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{t.max_users} users / {t.max_storage_mb} MB</td>
                  <td className="px-4 py-3 text-gray-400 text-xs">{format(new Date(t.created_at), 'MMM d, yyyy')}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => navigate(`/super-admin/tenants/${t.id}`)}
                        className="p-1.5 text-primary-600 hover:bg-primary-50 rounded" title="View">
                        <Eye className="w-4 h-4" />
                      </button>
                      {t.status !== 'suspended' && (
                        <button onClick={() => handleSuspend(t.id)}
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded" title="Suspend">
                          <Pause className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <p className="text-sm text-gray-500">Showing {((page-1)*limit)+1}–{Math.min(page*limit, total)} of {total}</p>
            <div className="flex gap-2">
              <button disabled={page === 1} onClick={() => setPage(p => p-1)} className="btn-secondary !py-1 !px-2 disabled:opacity-40">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button disabled={page === totalPages} onClick={() => setPage(p => p+1)} className="btn-secondary !py-1 !px-2 disabled:opacity-40">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TenantList;
